const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const IMPERSONATION_FLAG_KEY = 'impersonation';

/**
 * Single entry point for the auth tokens of the current browser tab.
 *
 * A regular session lives in localStorage ("remember me") or in sessionStorage. A tab opened by an
 * admin through "Switch to client view" is an impersonation tab: it holds the client's tokens in its
 * own sessionStorage only and never reads or writes localStorage, so the admin session kept there
 * stays active in every other tab.
 */
export const tokenStorage = {
  /** Whether the current tab is an admin impersonation tab. */
  isImpersonating: (): boolean => sessionStorage.getItem(IMPERSONATION_FLAG_KEY) === '1',

  getAccessToken: (): string | null =>
    tokenStorage.isImpersonating()
      ? sessionStorage.getItem(ACCESS_TOKEN_KEY)
      : localStorage.getItem(ACCESS_TOKEN_KEY) || sessionStorage.getItem(ACCESS_TOKEN_KEY),

  getRefreshToken: (): string | null =>
    tokenStorage.isImpersonating()
      ? sessionStorage.getItem(REFRESH_TOKEN_KEY)
      : localStorage.getItem(REFRESH_TOKEN_KEY) || sessionStorage.getItem(REFRESH_TOKEN_KEY),

  /**
   * Stores the tokens of a fresh login. `remember` picks localStorage over sessionStorage; an
   * impersonation tab always keeps them in its sessionStorage.
   */
  saveTokens: (accessToken: string, refreshToken: string, remember = true): void => {
    const storage = remember && !tokenStorage.isImpersonating() ? localStorage : sessionStorage;
    storage.setItem(ACCESS_TOKEN_KEY, accessToken);
    storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },

  /** Stores refreshed tokens in the storage that held the previous ones. */
  updateTokens: (accessToken: string, refreshToken: string): void => {
    const remembered = !tokenStorage.isImpersonating() && localStorage.getItem(REFRESH_TOKEN_KEY) !== null;
    tokenStorage.saveTokens(accessToken, refreshToken, remembered);
  },

  /**
   * Drops the session of the current tab. In an impersonation tab this ends the impersonation only:
   * the admin session in localStorage is left untouched and the tab falls back to it.
   */
  clearTokens: (): void => {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    if (tokenStorage.isImpersonating()) {
      sessionStorage.removeItem(IMPERSONATION_FLAG_KEY);
      return;
    }
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  /** Turns the current tab into an impersonation tab holding the given client tokens. */
  startImpersonation: (accessToken: string, refreshToken: string): void => {
    sessionStorage.setItem(IMPERSONATION_FLAG_KEY, '1');
    tokenStorage.saveTokens(accessToken, refreshToken, false);
  },
};
